// Plausible analytics — dormant until configured.
//
// The whole module is gated behind REACT_APP_PLAUSIBLE_DOMAIN. With no domain set
// at build time, no script is injected and every function here is a no-op — so the
// app ships with zero analytics, no Plausible account, and no cost.
//
// To turn it on later (or during Plausible's free trial):
//   1. Create the Plausible account and add the site `trychaptery.com`.
//   2. Set REACT_APP_PLAUSIBLE_DOMAIN=trychaptery.com in the build env and redeploy.
//   3. (Self-hosting only) also set REACT_APP_PLAUSIBLE_SRC to your script URL.
// UTM tags on QR links are captured automatically on the landing pageview.

type PlausibleFn = (
  event: string,
  options?: { props?: Record<string, unknown>; callback?: () => void },
) => void;

type PlausibleStub = PlausibleFn & { q?: unknown[][] };

declare global {
  interface Window {
    plausible?: PlausibleStub;
  }
}

const DOMAIN = process.env.REACT_APP_PLAUSIBLE_DOMAIN;
// Default to Plausible Cloud; override for a self-hosted instance. The `manual`
// variant lets us fire pageviews ourselves so SPA route changes are counted
// exactly once (no double-count of the initial load).
const SRC = process.env.REACT_APP_PLAUSIBLE_SRC || 'https://plausible.io/js/script.manual.js';

/** True when a domain is configured — handy for conditionally showing analytics UI. */
export const analyticsEnabled = !!DOMAIN;

let initialized = false;

/** Inject the Plausible script once. No-op when unconfigured or already loaded. */
export function initPlausible(): void {
  if (initialized || !DOMAIN || typeof document === 'undefined') return;
  initialized = true;

  // Queue stub: pageview/event calls made before the script finishes loading are
  // buffered on `.q` and replayed by Plausible once it loads (mirrors their snippet).
  if (!window.plausible) {
    const stub = ((...args: unknown[]) => {
      (stub.q = stub.q || []).push(args);
    }) as unknown as PlausibleStub;
    window.plausible = stub;
  }

  const script = document.createElement('script');
  script.defer = true;
  script.setAttribute('data-domain', DOMAIN);
  script.src = SRC;
  document.head.appendChild(script);
}

/** Record a pageview for the current URL. No-op when unconfigured. */
export function trackPageview(): void {
  if (!DOMAIN) return;
  window.plausible?.('pageview');
}

/**
 * Record a custom goal event, e.g. trackEvent('Trial Started', { source: 'qr' }).
 * Configure the matching goal in the Plausible dashboard to attribute conversions
 * back to the QR/UTM campaign. No-op when unconfigured.
 */
export function trackEvent(
  name: string,
  props?: Record<string, string | number | boolean>,
): void {
  if (!DOMAIN) return;
  window.plausible?.(name, props ? { props } : undefined);
}
